import { createOrganizationReference } from '../../../firebase/firebaseAPICalls/FirebaseAPICallsOrganization';
import { createTeamReference } from '../../../firebase/firebaseAPICalls/FirebaseAPICallsTeam';
import { getCurrentDateTime } from '../../DateTime';
import { SportsType } from '../../enums/SportsType';
import { createArticleReference } from '../../../firebase/firebaseAPICalls/FirebaseAPICallsArticle';
import { TagType } from '../../enums/TagType';
import { createGalleryReference } from '../../../firebase/firebaseAPICalls/FirebaseAPICallsGallery';
import { BaseballGameObject, BaseballPlayerStatObject } from '../../interface/gameInterface/baseball/BaseballGameInterface';
import { BaseballGamePlayerObject, getBaseballGamePlayerObjectFromBaseballGameObject } from '../../interface/gameInterface/baseball/BaseballGamePlayerInterface';
import { BaseballGameCoachObject, getBaseballGameCoachObjectFromBaseballGameObject } from '../../interface/gameInterface/baseball/BaseballGameCoachInterface';
import { createGameReference } from '../../../firebase/firebaseAPICalls/FirebaseAPICallsGame';
import { createSeasonReference } from '../../../firebase/firebaseAPICalls/FirebaseAPICallsSeason';
import { demoOpponentName } from '../OpponentDemo';
import { getDemoSeasonPreDevelopedSeasonId, getDemoSeasonPreDevelopedSeasonReference } from '../SeasonDemo';
import { createLeagueReference } from '../../../firebase/firebaseAPICalls/FirebaseAPICallsLeague';
import { createOpponentReference } from '../../../firebase/firebaseAPICalls/FirebaseAPICallsOpponent';
import { getDemoLeaguePreDevelopedConferenceId, getDemoLeaguePreDevelopedConferenceReference, getDemoLeaguePreDevelopedLeagueId, getDemoLeaguePreDevelopedLeagueReference } from '../LeagueDemo';
import { createGameTitle, demoGameAttendance, demoGameNotes, demoGameStatus, demoPromotionalText, demoPromotionalVideo } from './GameDemo';
import { demoTeamInfoStadium } from '../team/TeamInfoDemo';

export const demoBaseballGameFeatureImage = 'https://firebasestorage.googleapis.com/v0/b/optimx-sports.appspot.com/o/demo%2FdemoGame%2Fbaseball%2FdemoFeatureImage%2FdemoFeatureImage_1600x900.png?alt=media&token=b570520e-f984-47ae-b480-67b5ca848837';

export const getDemoBaseballGameInnings = (): Record<number, { our: number, opponent: number }> => {
    return {
        1: { our: 2, opponent: 1 },
        2: { our: 0, opponent: 0 },
        3: { our: 1, opponent: 2 },
        4: { our: 0, opponent: 0 },
        5: { our: 1, opponent: 0 },
        6: { our: 0, opponent: 0 },
        7: { our: 0, opponent: 0 },
        8: { our: 1, opponent: 0 },
        9: { our: 0, opponent: 0 },
    };
};

export const getDemoBaseballGameSimpleStats = () => {
    return {
        our: {
            R: 5,
            H: 8,
            E: 1,
            LOB: 6,
            AB: 35,
            '2B': 2,
            '3B': 0,
            HR: 1,
            RBI: 5,
            BB: 3,
            SO: 7,
            SB: 1,
            DP: 0,
        },
        opponent: {
            R: 3,
            H: 6,
            E: 2,
            LOB: 5,
            AB: 33,
            '2B': 1,
            '3B': 0,
            HR: 0,
            RBI: 3,
            BB: 2,
            SO: 8,
            SB: 0,
            DP: 1,
        },
    };
};

export const getDemoBaseballGamePlayerStats = (playerId: string): BaseballPlayerStatObject[] => {
    return [
        {
            id: playerId,
            name: 'John Smith',
            number: '7',
            position: 'SS',
            AB: 4,
            R: 2,
            H: 2,
            RBI: 2,
            BB: 1,
            IBB: 0,
            SO: 1,
            LOB: 1,
            '2B': 1,
            '3B': 0,
            HR: 1,
            SB: 1,
            CS: 0,
            HBP: 0,
            SH: 0,
            SF: 0,
            PO: 3,
            A: 4,
            E: 0,
        },
        {
            id: `${playerId}_2`,
            name: 'Mike Johnson',
            number: '22',
            position: 'CF',
            AB: 4,
            R: 1,
            H: 2,
            RBI: 1,
            BB: 0,
            IBB: 0,
            SO: 1,
            LOB: 2,
            '2B': 1,
            '3B': 0,
            HR: 0,
            SB: 0,
            CS: 0,
            HBP: 0,
            SH: 0,
            SF: 1,
            PO: 2,
            A: 0,
            E: 0,
        },
        {
            id: `${playerId}_3`,
            name: 'Tom Williams',
            number: '15',
            position: '1B',
            AB: 4,
            R: 1,
            H: 1,
            RBI: 1,
            BB: 1,
            IBB: 0,
            SO: 2,
            LOB: 1,
            '2B': 0,
            '3B': 0,
            HR: 0,
            SB: 0,
            CS: 0,
            HBP: 0,
            SH: 0,
            SF: 0,
            PO: 8,
            A: 1,
            E: 1,
        },
    ];
};

export const getDemoBaseballGamePlayer = (gameObject: BaseballGameObject, playerId: string): BaseballGamePlayerObject => {
    return getBaseballGamePlayerObjectFromBaseballGameObject(gameObject, playerId);
};

export const getDemoBaseballGameCoach = (gameObject: BaseballGameObject, coachId: string): BaseballGameCoachObject => {
    return getBaseballGameCoachObjectFromBaseballGameObject(gameObject, coachId);
};

export const createDemoBaseballGame = (
    organizationId: string,
    teamId: string,
    gameId: string,
    tagIds: string[],
    tagReferences: any[],
    tagTypes: TagType[],
    seasonId: string,
    articleId: string,
    galleryId: string,
    leagueId: string,
    opponentId: string,
    playerId: string,
    sportsType: SportsType,
): BaseballGameObject => {
    const gameReference = createGameReference(gameId);
    const leagueReference = createLeagueReference(teamId, leagueId);
    const seasonReference = createSeasonReference(teamId, seasonId);
    const opponentReference = createOpponentReference(teamId, opponentId);
    const articleReference = createArticleReference(articleId);
    const galleryReference = createGalleryReference(galleryId);
    const teamReference = createTeamReference(teamId);
    const organizationReference = createOrganizationReference(organizationId);
    const timeOfGame = new Date(2020, 12, 17, 0, 0, 0, 0);

    return {
        id: gameId,
        urlId: gameId,
        reference: gameReference,
        teamId,
        teamReference,
        organizationId,
        organizationReference,
        seasonId,
        seasonReference,
        preDevelopedSeasonId: getDemoSeasonPreDevelopedSeasonId(sportsType),
        preDevelopedSeasonReference: getDemoSeasonPreDevelopedSeasonReference(sportsType),
        leagueId,
        leagueReference,
        preDevelopedLeagueId: getDemoLeaguePreDevelopedLeagueId(sportsType),
        preDevelopedLeagueReference: getDemoLeaguePreDevelopedLeagueReference(sportsType),
        preDevelopedConferenceId: getDemoLeaguePreDevelopedConferenceId(sportsType),
        preDevelopedConferenceReference: getDemoLeaguePreDevelopedConferenceReference(sportsType),
        opponentId,
        opponentReference,
        articleId,
        articleReference,
        galleryId,
        galleryReference,
        title: createGameTitle(demoOpponentName, demoGameStatus, timeOfGame),
        sportsType,
        homeGame: true,
        status: demoGameStatus,
        timeOfGame,
        venue: demoTeamInfoStadium,
        attendance: demoGameAttendance,
        tagIds,
        tagReferences,
        tagTypes,
        featureImage: demoBaseballGameFeatureImage,
        articleImage: demoBaseballGameFeatureImage,
        winOrLoss: 'win',
        gameScore: '5-3',
        shutOut: false,
        innings: getDemoBaseballGameInnings(),
        simpleStats: getDemoBaseballGameSimpleStats(),
        playerStats: getDemoBaseballGamePlayerStats(playerId),
        winningPitcher: 'John Smith',
        losingPitcher: 'Mike Johnson',
        savePitcher: 'Tom Williams',
        promotionalText: demoPromotionalText,
        promotionalVideo: demoPromotionalVideo,
        notes: demoGameNotes,
        timeModified: getCurrentDateTime(),
        timeCreated: getCurrentDateTime(),
    };
};
